import classNames from "classnames";
import { FileIcon } from "components/design/FileIcon";
import ToolTip from "components/design/toolTip";
import { Button } from "components/DesignSystem/Button/Button";
import { YYYY_MM_DD } from "constants/date";
import { ADDRESS_AUTOFILL_VERSION_2_0 } from "constants/task";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { Formik } from "formik";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { useRoleBasedView } from "hooks/useRoleBasedView";
import { ReactNode, useMemo, useState } from "react";
import FilingInfoIcon from "static/images/FilingInfo.svg";
import { Preview } from "components/PreviewModal";
import {
  useGetTaskListForServiceTeamQuery,
  useGetTaskListForUserQuery,
} from "store/apis/taskList";
import {
  FIELD_TYPE,
  RepeatableField,
  Section,
  SectionField,
} from "types/Models/Filing";
import {
  FormDataConfiguration,
  Task,
  TaskCompletedDocuments,
} from "types/Models/task";
import Radio from "../FilingRadioGroup";
import { ReviewTaskConnectBank } from "./ReviewTaskConnectBank";
import { useCopyToClipboard } from "hooks/useCopyToClipboard";
import { MaskedValue } from "components/Entity/MaskedValue";

dayjs.extend(customParseFormat);

const SIMPLE_VALUE_TYPES = [
  FIELD_TYPE.DATE,
  FIELD_TYPE.EMAIL,
  FIELD_TYPE.TEXT,
  FIELD_TYPE.NUMBER,
  FIELD_TYPE.AMOUNT,
  FIELD_TYPE.PHONE,
  FIELD_TYPE.NON_US_ENTITY_SELECT,
  FIELD_TYPE.CHECKBOX,
  FIELD_TYPE.SELECT,
];

type SelectOption = {
  label: string;
  value: string;
};

export type FormControlProps = {
  authtoken: Record<string, unknown>;
  type: string;
  label: string;
  options?: (string | { label: string; value: string })[];
  tooltip?: string;
  descript?: string;
  defaultValue?: string | null;
  mandatory?: boolean;
  groupId: string;
  autofillKey?: string;
  autofillPrefix?: string;
  autofillSuffix?: string;
  updatedTask: Task;
  formId: string;
  document?: Record<string, unknown>;
  sectionKey: string;
  tag?: string;
  field: SectionField;
  entityId: string;
  section: Section;
  formData: FormDataConfiguration;
  fields?: SectionField[];
  season: string;
  deadline: string;
  selectedCurrency?: string;
  entityName?: string;
  name?: string;
  id?: string;
  placeholder?: string;
  disabled?: boolean;
};

const getAutofillValue = (
  autofillKey: string | undefined,
  section: Section,
  autofillDetailKey?: string
): string | undefined => {
  const details = section?.selected_autofill_details;

  if (autofillDetailKey) {
    return (
      details?.[autofillDetailKey as keyof typeof details] as
        | { autofill_string?: string }
        | undefined
    )?.autofill_string;
  }
  if (autofillKey === "addresses" || autofillKey === "address_details") {
    return details?.address_details?.autofill_string;
  }
  if (autofillKey === "bank_details") {
    return details?.bank_details?.autofill_string;
  }
  if (autofillKey === "persona_details") {
    return details?.persona_details?.autofill_string;
  }
  if (autofillKey === "shareholder_details") {
    return details?.shareholder_details?.autofill_string;
  }
};

const getRepeatableAutofillValue = (
  autofillKey: string | undefined,
  repeatableField?: RepeatableField
): string | undefined => {
  if (autofillKey === "addresses" || autofillKey === "address_details") {
    return repeatableField?.selected_autofill_details?.address_details
      ?.autofill_string;
  }
  if (autofillKey === "bank_details") {
    return repeatableField?.selected_autofill_details?.bank_details
      ?.autofill_string;
  }
  if (autofillKey === "persona_details") {
    return repeatableField?.selected_autofill_details?.persona_details
      ?.autofill_string;
  }
  if (autofillKey === "shareholder_details") {
    return repeatableField?.selected_autofill_details?.shareholder_details
      ?.autofill_string;
  }
};

const DocumentInfo = ({ file }: { file: TaskCompletedDocuments }) => {
  const { uuid: groupId } = useCurrentGroupContext();
  const [previewFileId, setPreviewFileId] = useState<string | null>(null);

  return (
    <>
      <span
        key={file.uuid}
        className="t-flex t-gap-2 t-cursor-pointer"
        onClick={() => setPreviewFileId(file?.uuid)}
      >
        <FileIcon fileType={file?.file_type} size="20" />
        <Button customType="link">{file.name}</Button>
      </span>
      <Preview
        showModal={Boolean(previewFileId)}
        closeModal={() => setPreviewFileId(null)}
        groupId={groupId}
        previewId={previewFileId}
      />
    </>
  );
};

export const FormTooltip = ({ tooltipText }: { tooltipText: string }) => (
  <ToolTip
    text={<span dangerouslySetInnerHTML={{ __html: tooltipText }} />}
    side="right"
  >
    <img src={FilingInfoIcon} alt="info" className="t-ml-1" />
  </ToolTip>
);

export const FormLabel = ({
  label,
  tooltip,
  required,
}: {
  label: string;
  tooltip?: string;
  required?: boolean;
}) => (
  <div>
    <label
      className={classNames("t-text-body-sm t-text-text-30", {
        "after:t-font-bold after:t-text-red after:t-content-['_*']": required,
      })}
    >
      {label}
    </label>
    {tooltip && <FormTooltip tooltipText={tooltip} />}
  </div>
);

export const FormValue = ({
  value,
}: {
  value?: string | number | null | ReactNode;
}) => (
  <div className="t-text-body-sm t-text-text-100">
    {Boolean(value) ? value : "-"}
  </div>
);

export const FormValueContainer = ({ children }: { children?: ReactNode }) => (
  <div className="t-space-y-0.5">{children}</div>
);

export const ReviewFormControl = ({
  authtoken,
  type,
  label,
  options = [],
  tooltip,
  descript,
  defaultValue,
  mandatory,
  groupId,
  autofillKey,
  autofillPrefix,
  autofillSuffix,
  updatedTask,
  formId,
  sectionKey,
  field,
  entityId,
  section,
  formData,
  fields,
  season,
  deadline,
  selectedCurrency,
  entityName,
  ...props
}: FormControlProps) => {
  const { isCustomer, isAdmin, isAnyServiceUser } = useRoleBasedView();

  const fieldValue = field.value ?? defaultValue ?? "";

  const [localOptions, setLocalOptions] = useState<SelectOption[]>(() => {
    const opts = options.map((o) =>
      typeof o === "object" ? o : { label: o, value: o }
    );
    if (fieldValue && !opts.some((o) => o.value === fieldValue)) {
      return [...opts, { label: fieldValue, value: fieldValue }];
    }
    return opts;
  });

  const netProfit = useMemo(() => {
    const totalRevenue = fields?.find(
      (f) => f.field_key === "total_revenue"
    )?.value;
    const totalExpense = fields?.find(
      (f) => f.field_key === "total_expense"
    )?.value;
    const rAndDExpense =
      fields?.find((f) => f.field_key === "r_and_d_expense")?.value ?? "0";

    if (!totalRevenue || !totalExpense) return null;

    return (
      Number(totalRevenue) -
      (Number(totalExpense) - Number(rAndDExpense)) -
      Number(rAndDExpense) / 15
    );
  }, [fields]);

  const {
    data: userTaskData,
    isLoading: isUserTaskLoading,
    isFetching: isUserTaskFetching,
  } = useGetTaskListForUserQuery(
    {
      baseTaskKey: field.control_basetask?.join(","),
      seasons: season,
      showOngoing: true,
    },
    {
      skip:
        isAnyServiceUser ||
        !field.control_basetask ||
        !season ||
        field.control_basetask?.length === 0,
    }
  );

  const {
    data: cpaTaskList,
    isLoading: isCPATaskLoading,
    isFetching: isAdminTaskFetching,
  } = useGetTaskListForServiceTeamQuery(
    {
      baseTaskKey: field.control_basetask?.join(","),
      groupId,
      seasons: season,
      showOngoing: true,
    },
    {
      skip:
        isCustomer ||
        !groupId ||
        !season ||
        !field.control_basetask ||
        field.control_basetask?.length === 0,
    }
  );

  const count =
    (userTaskData || cpaTaskList)?.tasks?.filter(
      (task) => task.entity?.uuid === entityId
    )?.length || 0;

  if (isUserTaskLoading || isCPATaskLoading) {
    return (
      <div className="t-bg-neutral-10 t-w-full t-my-2 t-h-5 t-animate-pulse" />
    );
  }

  if (
    (field.control_basetask_for === "CUSTOMER" && isCustomer) ||
    (field.control_basetask_for === "ADMIN" && isAdmin) ||
    !field.control_basetask_for
  ) {
    if (field.control_basetask && field.control_basetask.length > 0) {
      if (field.control_basetask_show && count == 0) return <></>;
      if (!field.control_basetask_show && count > 0) return <></>;
    }
  }

  if (field.should_show_on_profit && (!netProfit || netProfit < 0)) {
    return <></>;
  }

  if (
    (field.is_hidden_for_customer && isCustomer) ||
    (field.is_hidden_for_admin && !isCustomer)
  ) {
    return null;
  }

  let autofillKeys: string[] = [];

  if (autofillKey && !autofillSuffix) {
    autofillKeys =
      section.section_fields?.reduce<string[]>((ac, cv) => {
        if (cv.autofill_section === field.autofill_section && cv.field_key) {
          const keyWithoutPrefix = cv.field_key.replace(
            `${field.autofill_prefix}_`,
            ""
          );
          if (keyWithoutPrefix) return [...ac, keyWithoutPrefix];
        }
        return ac;
      }, []) ?? [];
  }

  if (autofillKey && autofillSuffix) {
    autofillKeys =
      section.repeatable_fields
        ?.find(({ id }) => id == Number(autofillSuffix))
        ?.fields?.reduce<string[]>((ac, cv) => {
          if (cv.autofill_section === field.autofill_section && cv.field_key) {
            const keyWithoutPrefix = cv.field_key.replace(
              `${field.autofill_prefix}_`,
              ""
            );
            if (keyWithoutPrefix) return [...ac, keyWithoutPrefix];
          }
          return ac;
        }, []) ?? [];
  }

  const year = season ? season : dayjs().year() - 1;

  if (type === "action") {
    switch (field?.cta?.cta_action) {
      case "add_bank_account": {
        return (
          <ReviewTaskConnectBank
            entityId={entityId}
            groupId={groupId}
            openAccordionByDefault={false}
            date={dayjs(`${year}-12-31`).format(YYYY_MM_DD)}
          />
        );
      }

      default: {
        return <></>;
      }
    }
  }

  if (type === FIELD_TYPE.SELECT_SHAREHOLDER_AUTOFILL) {
    const totalCount = section.repeatable_fields?.length || 0;
    return (
      <div className="t-space-y-5">
        {section?.selected_autofill_details?.shareholder_details && (
          <div className="t-space-y-5">
            <div className="t-space-y-2">
              <div className="t-text-body-sm t-text-text-100">
                {section?.selected_autofill_details?.shareholder_details?.name}{" "}
                {section?.selected_autofill_details?.shareholder_details
                  ?.ownership_stake && (
                  <span className="t-text-body-sm t-text-text-100">
                    (
                    {
                      section?.selected_autofill_details?.shareholder_details
                        ?.ownership_stake
                    }
                    % Share)
                  </span>
                )}
              </div>
              <div className="t-text-body-sm t-text-text-60">
                {
                  section?.selected_autofill_details?.shareholder_details
                    ?.autofill_string!
                }
              </div>
              <div className="t-flex t-mt-3">
                <div className="t-flex-1">
                  <FormValueContainer>
                    <FormLabel {...field} label="TIN" />
                    <FormValue
                      value={
                        section?.selected_autofill_details?.shareholder_details
                          ?.tin ? (
                          <MaskedValue
                            value={
                              section?.selected_autofill_details
                                ?.shareholder_details?.tin
                            }
                          />
                        ) : (
                          "-"
                        )
                      }
                    />
                  </FormValueContainer>
                </div>
                <div className="t-flex-1">
                  <FormValueContainer>
                    <FormLabel {...field} label="Citizenship" />
                    <FormValue
                      value={
                        section?.selected_autofill_details?.shareholder_details
                          ?.citizenship
                      }
                    />
                  </FormValueContainer>
                </div>
              </div>
              <div className="t-flex t-mt-3">
                <div className="t-flex-1">
                  <FormValueContainer>
                    <FormLabel {...field} label="Tax Residency" />
                    <FormValue
                      value={
                        section?.selected_autofill_details?.shareholder_details
                          ?.country?.name
                      }
                    />
                  </FormValueContainer>
                </div>
              </div>
              <div className="t-mt-3">
                <FormValueContainer>
                  <FormLabel {...field} label="Address" />
                  <FormValue
                    value={
                      section?.selected_autofill_details?.shareholder_details
                        ?.address?.autofill_string
                    }
                  />
                </FormValueContainer>
              </div>
            </div>
          </div>
        )}
        {totalCount > 0 &&
          section.repeatable_fields?.map((repeatableField, index) => {
            return (
              <div key={index} className="t-space-y-5">
                <div className="t-space-y-2">
                  <div className="t-text-body-sm t-font-light t-italic t-text-text-30">
                    {section.section_title} {index + 1}
                  </div>
                  <div className="t-text-body-sm t-text-text-100">
                    {
                      repeatableField?.selected_autofill_details
                        ?.shareholder_details?.name
                    }{" "}
                    {repeatableField?.selected_autofill_details
                      ?.shareholder_details?.ownership_stake && (
                      <span className="t-text-body-sm t-text-text-100">
                        (
                        {
                          repeatableField?.selected_autofill_details
                            ?.shareholder_details?.ownership_stake
                        }
                        % Share)
                      </span>
                    )}
                  </div>
                  <div className="t-text-body-sm t-text-text-60">
                    {
                      repeatableField?.selected_autofill_details
                        ?.address_autofill_string!
                    }
                  </div>
                  <div className="t-flex t-mt-3">
                    <div className="t-flex-1">
                      <FormValueContainer>
                        <FormLabel {...field} label="TIN" />
                        <FormValue
                          value={
                            repeatableField?.selected_autofill_details
                              ?.shareholder_details?.tin ? (
                              <MaskedValue
                                value={
                                  repeatableField?.selected_autofill_details
                                    ?.shareholder_details?.tin
                                }
                              />
                            ) : (
                              "-"
                            )
                          }
                        />
                      </FormValueContainer>
                    </div>
                    <div className="t-flex-1">
                      <FormValueContainer>
                        <FormLabel {...field} label="Citizenship" />
                        <FormValue
                          value={
                            repeatableField?.selected_autofill_details
                              ?.shareholder_details?.citizenship
                          }
                        />
                      </FormValueContainer>
                    </div>
                  </div>
                  <div className="t-flex t-mt-3">
                    <div className="t-flex-1">
                      <FormValueContainer>
                        <FormLabel {...field} label="Tax Residency" />
                        <FormValue
                          value={
                            repeatableField?.selected_autofill_details
                              ?.shareholder_details?.country?.name
                          }
                        />
                      </FormValueContainer>
                    </div>
                  </div>
                  <div className="t-mt-3">
                    <FormValueContainer>
                      <FormLabel {...field} label="Address" />
                      <FormValue
                        value={
                          repeatableField?.selected_autofill_details
                            ?.shareholder_details?.address?.autofill_string
                        }
                      />
                    </FormValueContainer>
                  </div>
                </div>
                {index !== totalCount - 1 && (
                  <div className="t-w-2/3 t-border t-border-solid t-border-t-0 t-border-neutral-0"></div>
                )}
              </div>
            );
          })}
      </div>
    );
  }

  if (
    type === FIELD_TYPE.SELECT_BANK_AUTOFILL &&
    formData.data.data.address_autofill_version === ADDRESS_AUTOFILL_VERSION_2_0
  ) {
    return (
      <div className="t-grid t-gap-x-20 t-gap-y-4 t-grid-cols-2">
        {autofillKeys
          .filter((keyToFill) => !keyToFill.includes("autofill"))
          ?.map((keyToFill, index) => {
            const fieldData = fields?.find((field: SectionField) =>
              field.field_key?.includes(keyToFill)
            );

            if (
              !fieldData ||
              fieldData?.type?.includes("autofill") ||
              fieldData?.field_key?.includes("last_name")
            ) {
              return null;
            }

            if (fieldData?.field_key?.includes("first_name")) {
              const lastName = fields?.find((field: SectionField) =>
                field.field_key?.includes("last_name")
              )?.value;

              return (
                <FormValueContainer key={index}>
                  <FormLabel {...field} label="Full Name" />
                  <FormValue value={fieldData.value + " " + lastName} />
                </FormValueContainer>
              );
            }

            return (
              <FormValueContainer key={index}>
                <FormLabel {...field} label={fieldData.field_label} />
                <FormValue value={fieldData.value} />
              </FormValueContainer>
            );
          })}
      </div>
    );
  }

  if (
    type === FIELD_TYPE.SELECT_AUTOFILL &&
    formData.data.data.address_autofill_version === ADDRESS_AUTOFILL_VERSION_2_0
  ) {
    const value =
      autofillSuffix && !isNaN(Number(autofillSuffix))
        ? getRepeatableAutofillValue(
            field.autofill_key,
            section.repeatable_fields?.[Number(autofillSuffix)]
          )
        : getAutofillValue(
            field.autofill_key,
            section,
            field.autofill_detail_key
          );

    return (
      <FormValueContainer>
        <FormLabel {...field} label={field.field_label} />
        <FormValue value={value} />
      </FormValueContainer>
    );
  }

  if (
    type === FIELD_TYPE.SELECT_DIRECTORS_AUTOFILL &&
    formData.data.data.address_autofill_version === ADDRESS_AUTOFILL_VERSION_2_0
  ) {
    const isDirectorDataExists = section.repeatable_fields?.[0]?.fields?.some(
      (field: { value: any; field_key: any }) =>
        Boolean(field.value) && !field.field_key.includes("director_country")
    );

    if (!isDirectorDataExists) {
      return "-";
    }

    const totalCount = section.repeatable_fields?.length || 0;

    return (
      <div className="t-space-y-5">
        {section.repeatable_fields?.map((repeatableField, index) => {
          return (
            <div key={index} className="t-space-y-5">
              <div className="t-space-y-2">
                <div className="t-text-body-sm t-font-light t-italic t-text-text-30">
                  Director {index + 1}
                </div>
                <div className="t-text-body-sm t-text-text-100">
                  {
                    repeatableField?.selected_autofill_details
                      ?.persona_autofill_string
                  }
                </div>
                <div className="t-text-body-sm t-text-text-60">
                  {
                    repeatableField?.selected_autofill_details
                      ?.address_autofill_string!
                  }
                </div>
              </div>
              {index !== totalCount - 1 && (
                <div className="t-w-2/3 t-border t-border-solid t-border-t-0 t-border-neutral-0"></div>
              )}
            </div>
          );
        })}
      </div>
    );
  }

  if (
    type === FIELD_TYPE.SELECT_AUTHORIZED_SIGNATORY_AUTOFILL &&
    formData.data.data.address_autofill_version === ADDRESS_AUTOFILL_VERSION_2_0
  ) {
    return (
      <div className="t-space-y-2">
        <div className="t-text-body-sm t-text-text-30 t-font-light t-italic">
          Name & address
        </div>
        <div className="t-text-body-sm t-text-text-100">
          {(section?.selected_autofill_details?.persona_details?.first_name ||
            "") +
            " " +
            (section?.selected_autofill_details?.persona_details?.last_name ||
              "")}
          {section?.selected_autofill_details?.designation && (
            <span className="t-text-body-sm t-text-text-100">
              {" "}
              ({section?.selected_autofill_details?.designation})
            </span>
          )}
        </div>
        <div className="t-text-body-sm t-text-text-30">
          {section?.selected_autofill_details?.address_details?.autofill_string}
        </div>
      </div>
    );
  }

  if (
    type === FIELD_TYPE.SELECT_PRINCIPAL_OFFICER_AUTOFILL &&
    formData.data.data.address_autofill_version === ADDRESS_AUTOFILL_VERSION_2_0
  ) {
    return (
      <div className="t-space-y-2">
        <div className="t-text-body-sm t-text-text-30 t-font-light t-italic">
          Name & address
        </div>
        <div className="t-text-body-sm t-text-text-100">
          {(section?.selected_autofill_details?.persona_details?.first_name ||
            "") +
            " " +
            (section?.selected_autofill_details?.persona_details?.last_name ||
              "")}
          {section?.selected_autofill_details?.designation && (
            <span> ({section?.selected_autofill_details?.designation})</span>
          )}
        </div>
        <div className="t-text-body-sm t-text-text-30">
          {section?.selected_autofill_details?.address_details?.autofill_string}
        </div>
      </div>
    );
  }

  if (type === FIELD_TYPE.MULTISELECT) {
    const value =
      fieldValue && fieldValue.length > 0
        ? localOptions
            .filter((o) => fieldValue.includes(o.value))
            .map((option) =>
              typeof option === "object" ? option.value : option
            )
            ?.join(", ")
        : "-";

    return (
      <FormValueContainer>
        <FormLabel {...field} label={field.field_label} />
        <FormValue value={value} />
      </FormValueContainer>
    );
  }

  if (type === FIELD_TYPE.RADIO) {
    return (
      <FormValueContainer>
        <FormLabel {...field} label={field.field_label} />
        <Formik initialValues={{}} onSubmit={() => {}}>
          <Radio.Root defaultValue={field.value ?? undefined} disabled>
            <Radio.Content className="t-mt-2 t-space-y-1">
              {options?.map((ele) => {
                const optionValue = typeof ele === "object" ? ele.value : ele;
                const optionLabel = typeof ele === "object" ? ele.label : ele;

                return (
                  <Radio.Item
                    asChild
                    inline={false}
                    value={optionValue}
                    key={optionValue}
                    disabled
                  >
                    <div className="t-text-body-sm t-text-text-100 t-w-full t-text-left">
                      {optionLabel}
                    </div>
                  </Radio.Item>
                );
              })}
            </Radio.Content>
          </Radio.Root>
        </Formik>
      </FormValueContainer>
    );
  }

  if (type === FIELD_TYPE.PASSWORD) {
    return (
      <FormValueContainer>
        <FormLabel {...field} label={field.field_label} />
        <FormValue value={field.value?.replace(/./g, "*")} />
      </FormValueContainer>
    );
  }

  if (type === FIELD_TYPE.FILE_UPLOAD) {
    return (
      <FormValueContainer>
        <FormLabel
          required={field.required}
          label={field.field_label}
          tooltip={tooltip}
        />
        {field?.documents?.length && field?.documents?.length > 0 ? (
          <div className="t-space-y-2 t-flex t-flex-col">
            {field.documents.map((file) => (
              <DocumentInfo
                key={(file as TaskCompletedDocuments).uuid}
                file={file as TaskCompletedDocuments}
              />
            ))}
          </div>
        ) : (
          "-"
        )}
      </FormValueContainer>
    );
  }

  const isSelectPersonaAutofill =
    type === FIELD_TYPE.SELECT_PERSONA_AUTOFILL &&
    formData.data.data.address_autofill_version ===
      ADDRESS_AUTOFILL_VERSION_2_0;

  if (
    SIMPLE_VALUE_TYPES.includes(type as (typeof SIMPLE_VALUE_TYPES)[number]) ||
    isSelectPersonaAutofill
  ) {
    return (
      <FormValueContainer>
        <FormLabel {...field} label={field.field_label} />
        <FormValue value={field.value} />
      </FormValueContainer>
    );
  }

  return null;
};
